// ── Shareable permalinks: the whole view state serialized into location.hash ──
// "Send someone the exact view" is the distribution loop for a public learning
// tool: a teacher shares Katrina at hour 124 with the surge footprint on; a
// forecaster shares a drawn climatology box over the ensemble cluster. All view
// state already lives in the one Zustand store, so this needs no router —
// subscribe → debounce-serialize a whitelist → history.replaceState, and
// hydrate on boot (the async storm loaders are idempotent with stale guards).

import { useStore, type Mode, type SatLayer } from "./store";
import type { Box } from "../engine/types";
import { getMapHandle } from "../map/mapHandle";

const boxToStr = (b: Box) => [b.lat_min, b.lat_max, b.lon_min, b.lon_max].map((v) => v.toFixed(2)).join(",");
const strToBox = (s: string): Box | null => {
  const p = s.split(",").map(Number);
  if (p.length !== 4 || p.some((v) => !Number.isFinite(v))) return null;
  return { lat_min: p[0], lat_max: p[1], lon_min: p[2], lon_max: p[3] };
};

function serialize(): string {
  const s = useStore.getState();
  const q = new URLSearchParams();
  if (s.mode !== "live") q.set("m", s.mode);

  if (s.boxA) q.set("ba", boxToStr(s.boxA));
  if (s.boxB) q.set("bb", boxToStr(s.boxB));
  if (s.boxC) q.set("bc", boxToStr(s.boxC));
  const f = s.filters;
  if (f.yearMin !== 1851) q.set("y0", String(f.yearMin));
  if (f.yearMax !== 2025) q.set("y1", String(f.yearMax));
  if (f.oniRange[0] !== -3 || f.oniRange[1] !== 3) q.set("oni", `${f.oniRange[0]},${f.oniRange[1]}`);
  if (f.monthFilter.length) q.set("mo", f.monthFilter.join(","));
  if (s.climoOverlay) q.set("gw", "1");

  if (s.live.selectedId) q.set("lv", s.live.selectedId);
  if (s.live.showGenesisDots) q.set("gd", "1");
  if (s.live.shownEnsembles.length) q.set("es", s.live.shownEnsembles.join(","));
  if (s.live.ensembleAlpha !== 1) q.set("ea", s.live.ensembleAlpha.toFixed(1));
  if (s.explorer.selectedId) { q.set("ex", s.explorer.selectedId); q.set("exh", String(s.explorer.hour)); }
  if (s.focus.stormId) { q.set("fc", s.focus.stormId); q.set("fch", String(s.focus.hour)); }
  if (s.compare.stormId) { q.set("cp", s.compare.stormId); q.set("cph", String(s.compare.hour)); }

  const o = s.overlays;
  if (o.satellite !== "off") q.set("sat", o.satellite);
  if (o.radar) q.set("radar", "1");
  if (o.motion) q.set("motion", "1");
  if (o.opacity !== 0.85) q.set("op", o.opacity.toFixed(2));
  if (o.loopMin !== 90) q.set("loop", String(o.loopMin));
  if (o.ocean !== "off") q.set("oc", o.ocean);
  if (o.lightning) q.set("ltg", "1");
  if (o.meso) q.set("meso", "1");

  const map = getMapHandle();
  if (map) {
    const c = map.getCenter();
    q.set("cam", `${c.lat.toFixed(3)},${c.lng.toFixed(3)},${map.getZoom().toFixed(2)}`);
  }
  return q.toString();
}

let _timer: ReturnType<typeof setTimeout> | null = null;
export function schedulePermalinkWrite() {
  if (_timer) clearTimeout(_timer);
  _timer = setTimeout(() => {
    try {
      history.replaceState(null, "", `#${serialize()}`);
    } catch { /* ignore (very old browsers / privacy modes) */ }
  }, 500);
}

/** Start mirroring store changes into the hash. Call once after boot. */
export function initPermalinks() {
  useStore.subscribe(() => schedulePermalinkWrite());
}

/** Rebuild the view from a shared hash. Call once after the base data loads. */
export async function hydrateFromHash(): Promise<void> {
  const raw = location.hash.replace(/^#/, "");
  if (!raw) return;
  const q = new URLSearchParams(raw);
  const st = useStore.getState();

  // sync state first
  const mode = q.get("m") as Mode | null;
  if (mode === "live" || mode === "explorer" || mode === "climatology") st.setMode(mode);
  // links from before live became the default carry no "m" for climatology —
  // their boxes/filters still say where they belong
  else if (["ba", "bb", "bc", "y0", "y1", "oni", "mo"].some((k) => q.has(k))) st.setMode("climatology");
  const patch: Record<string, unknown> = {};
  const ba = q.get("ba") && strToBox(q.get("ba")!);
  const bb = q.get("bb") && strToBox(q.get("bb")!);
  const bc = q.get("bc") && strToBox(q.get("bc")!);
  if (ba) patch.boxA = ba;
  if (bb) patch.boxB = bb;
  if (bc) patch.boxC = bc;
  if (Object.keys(patch).length) useStore.setState(patch);
  st.setFilters({
    ...(q.get("y0") ? { yearMin: Number(q.get("y0")) } : {}),
    ...(q.get("y1") ? { yearMax: Number(q.get("y1")) } : {}),
    ...(q.get("oni") ? { oniRange: q.get("oni")!.split(",").map(Number) as [number, number] } : {}),
    ...(q.get("mo") ? { monthFilter: q.get("mo")!.split(",").map(Number) } : {}),
  });
  const sat = q.get("sat") as SatLayer | null;
  useStore.setState({
    overlays: {
      ...useStore.getState().overlays,
      ...(sat === "geocolor" || sat === "ir" || sat === "wv" || sat === "vis" ? { satellite: sat } : {}),
      radar: q.get("radar") === "1",
      motion: q.get("motion") === "1",
      ...(q.get("op") ? { opacity: Number(q.get("op")) } : {}),
      ...([60, 90, 180].includes(Number(q.get("loop"))) ? { loopMin: Number(q.get("loop")) } : {}),
      ...(q.get("oc") === "sst" || q.get("oc") === "ohc" ? { ocean: q.get("oc") as "sst" | "ohc" } : {}),
      lightning: q.get("ltg") === "1",
      meso: q.get("meso") === "1",
    },
  });

  // the ensembles a shared view shows (older links listed hidden ones, "eh":
  // those open on the deterministic tracks, like any storm)
  if (q.get("es")) {
    useStore.setState({
      live: { ...useStore.getState().live, shownEnsembles: q.get("es")!.split(",").filter(Boolean) },
    });
  }

  if (q.get("gd") === "1") {
    useStore.setState({ live: { ...useStore.getState().live, showGenesisDots: true } });
  }

  if (q.get("ea")) {
    const ea = Number(q.get("ea"));
    if (Number.isFinite(ea) && ea >= 0.5 && ea <= 3) {
      useStore.setState({ live: { ...useStore.getState().live, ensembleAlpha: ea } });
    }
  }

  // async loaders (idempotent, stale-guarded)
  const jobs: Promise<unknown>[] = [];
  if (q.get("lv") || q.get("gw")) {
    jobs.push(
      st.loadLiveIndex().then(async () => {
        if (q.get("gw")) useStore.setState({ climoOverlay: true });
        if (q.get("lv")) await useStore.getState().selectLiveStorm(q.get("lv")!, { keepEnsembles: true });
      }),
    );
  }
  if (q.get("ex")) {
    jobs.push(
      st.loadExplorerIndex().then(async () => {
        await useStore.getState().selectExplorerStorm(q.get("ex")!);
        if (q.get("exh")) useStore.getState().setHour(Number(q.get("exh")));
      }),
    );
  }
  if (q.get("cp")) {
    jobs.push(
      useStore.getState().selectCompareStorm(q.get("cp")!).then(() => {
        if (q.get("cph")) useStore.getState().setCompareHour(Number(q.get("cph")));
      }),
    );
  }
  if (q.get("fc")) {
    jobs.push(
      useStore.getState().focusStorm(q.get("fc")!).then(() => {
        if (q.get("fch")) useStore.getState().setFocusHour(Number(q.get("fch")));
      }),
    );
  }
  await Promise.allSettled(jobs);

  // camera LAST so the loaders' auto-fits don't stomp the shared view
  const cam = q.get("cam");
  if (cam) {
    const [lat, lng, zoom] = cam.split(",").map(Number);
    if ([lat, lng, zoom].every(Number.isFinite)) {
      for (let i = 0; i < 20; i++) {
        const map = getMapHandle();
        if (map) {
          // wait out the ~800ms auto-fit eases, then take the camera
          setTimeout(() => map.jumpTo({ center: [lng, lat], zoom }), 900);
          break;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
    }
  }
}
